import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { useSession } from "@otto/plattform";
import { Shell } from "./shared/components/Shell.js";
import { Page } from "./shared/components/Page.js";
import { SignIn } from "./features/auth/components/SignIn.js";
import { NoAccess } from "./features/auth/components/NoAccess.js";
import { Results } from "./features/results/components/Results.js";
import { StudentDetail } from "./features/results/components/StudentDetail.js";
import { ClassDetail } from "./features/codes/components/ClassDetail.js";
import { ClassList } from "./features/classes/components/ClassList.js";
import { ClassSettings } from "./features/courses/components/ClassSettings.js";
import { School } from "./features/school/components/School.js";
import { TourProvider } from "./shared/context/TourContext.js";
import styles from "./shared/components/shell.module.css";

/** Alte Adresse einer Klasse: sie zeigte die Codes und tut es weiter. */
function ToCodes() {
  const { classId = "" } = useParams();
  return <Navigate to={`/classes/${classId}/codes`} replace />;
}

export function App() {
  const { state } = useSession();

  if (state.kind === "loading") {
    return <p className={styles.empty}>Einen Moment …</p>;
  }
  if (state.kind === "signedOut") {
    return <SignIn />;
  }

  // Angemeldet, aber die Rechte waren nicht zu ermitteln – Netz, Uhrendrift,
  // Server kurz weg. Das ist ausdrücklich NICHT „kein Zugang“: wer das
  // verwechselt, schickt eine berechtigte Nutzerin zur Schulleitung, obwohl sie
  // nur einmal neu laden müsste.
  if (state.kind === "failed") {
    return (
      <div className={styles.centered}>
        <div className={styles.signInCard}>
          <h1 className={styles.title}>Gerade nicht erreichbar</h1>
          <p className={styles.subtitle}>
            Die Anmeldung hat geklappt, aber Ihre Berechtigung war nicht abzurufen. Das
            ist kein Problem mit Ihrem Konto – meist genügt ein erneuter Versuch.
          </p>
          <button
            type="button"
            className={styles.button}
            onClick={() => window.location.reload()}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  // Angemeldet, aber kein Schulprofil: entweder ein Plattformkonto (das gehört
  // in die Admin-App) oder ein Konto ohne Zuordnung. Beides braucht eine Erklärung und
  // kein leeres Dashboard.
  if (!state.identity.profile) {
    return <NoAccess identity={state.identity} />;
  }

  const profile = state.identity.profile;
  const isSchoolAdmin = profile.role === "school_admin";

  return (
    // Die Führung durch die ersten Schritte liegt UM die Routen: sie beginnt auf
    // /classes, geht auf die Codeseite weiter und darf dabei nicht verloren
    // gehen. Und sie liegt hinter den Zugangsfällen darüber – ein Plattformkonto
    // und ein Konto ohne Schule sehen hier eine Erklärung und kein Dashboard,
    // also auch keine Führung.
    <TourProvider
      schoolId={profile.schoolId}
      userId={state.identity.user.id}
      role={profile.role}
    >
      <Routes>
        {/* Die Breite steht an der Route, nicht in der Seite: hier sieht man alle
          Seiten untereinander und prüft die Zuordnung auf einen Blick. */}
        <Route element={<Shell />}>
          <Route
            path="/classes"
            element={
              <Page>
                <ClassList />
              </Page>
            }
          />
          {/* Die Codes haben eine eigene Adresse wie die Ergebnisse und die
            Einstellungen: drei Geschwister unter der Klasse, nicht eines davon
            als die Klasse selbst. Die alte Adresse bleibt und leitet weiter –
            die Lehrkräfte haben Lesezeichen. */}
          <Route
            path="/classes/:classId/codes"
            element={
              <Page>
                <ClassDetail />
              </Page>
            }
          />
          <Route path="/classes/:classId" element={<ToCodes />} />
          <Route
            path="/classes/:classId/results"
            element={
              <Page>
                <Results />
              </Page>
            }
          />
          {/* Der Übungscode steht in der Adresse: er ist kein personenbezogenes
            Datum, und die Ansicht eines Kindes soll sich verlinken lassen. */}
          <Route
            path="/classes/:classId/results/:code"
            element={
              <Page>
                <StudentDetail />
              </Page>
            }
          />
          <Route
            path="/classes/:classId/settings"
            element={
              <Page>
                <ClassSettings />
              </Page>
            }
          />
          {/* Der Bereich gehört der Schulleitung, und zwar nicht nur im Menü.
            Die Leiste blendete den Link für Lehrkräfte aus, die Adresse ließ
            sie trotzdem herein: dort standen dann Lizenz und ein Formular zum
            Einladen. Die Edge Function hätte die Einladung mit 403 abgelehnt –
            aber ein Formular anzubieten, das der Server verweigert, ist eine
            Zumutung und sieht aus wie ein Loch. */}
          <Route
            path="/school"
            element={
              isSchoolAdmin ? (
                <Page>
                  <School />
                </Page>
              ) : (
                <Navigate to="/classes" replace />
              )
            }
          />
          <Route path="*" element={<Navigate to="/classes" replace />} />
        </Route>
      </Routes>
    </TourProvider>
  );
}
